import { redirect, type AnyRedirect } from '@tanstack/react-router'

import { sanitizeRedirectTarget } from './sanitize-redirect-target.ts'

/**
 * Bounces an unauthenticated or unauthorized user to the login page, keeping the page they wanted (`redirect`) and
 * flagging the bounce (`from_unauthorized_code`). For route guards and loaders.
 *
 * Redirects with `to`, never `href`: a preload ignores `href` and reruns the guard without end (TanStack/router#7141).
 */
export function createLoginRedirect(currentHref: string): AnyRedirect {
  return redirect({ to: '/login', search: { redirect: loginReturnTarget(currentHref), from_unauthorized_code: true } })
}

/**
 * The same login destination as {@link createLoginRedirect}, as an href for a full page load outside the router
 * (the tRPC error link, `redirectToLogin` in client.ts). Both share one return target, so they stay consistent.
 */
export function buildLoginHref(
  currentHref: string = window.location.href,
  origin: string = window.location.origin,
): string {
  const loginUrl = new URL('/login', origin)
  const target = loginReturnTarget(currentHref, origin)
  if (target) {
    loginUrl.searchParams.set('redirect', target)
  }
  loginUrl.searchParams.set('from_unauthorized_code', 'true')
  return loginUrl.pathname + loginUrl.search
}

/**
 * Runs the target through {@link sanitizeRedirectTarget}: same-origin, relative, without transient params. On the
 * login page it keeps the original target, so bouncing again never nests the redirect into a loop.
 */
function loginReturnTarget(currentHref: string, origin: string = window.location.origin): string | undefined {
  const current = new URL(currentHref, origin)
  const rawTarget = current.pathname === '/login' ? current.searchParams.get('redirect') : current.href
  return sanitizeRedirectTarget(rawTarget, origin) ?? undefined
}
